import { ADMIN_AREAS, canOpenArea } from '@/features/admin/shared/contract'
import { SEARCH_ALL_MIN, type RelatedRef, type SearchGroup, type SearchItem } from '@/features/admin/shared/search-contract'
import { normalize } from '@/features/erp/entity-search'
import { MAIN_PAGES_NAV } from './nav'
import type { AdminRole } from '@/features/admin/shared/permissions'
import type { ConfigSection } from '@/features/erp/config/registry'
import { experienceOfView, experienceTreeTabs, treeNodeAllowed, type NavEntry } from '@/features/erp/frentes'

export { normalize, searchEntities } from '@/features/erp/entity-search'

/* COG-009 — pure helpers of the ⌘K command palette (CommandPalette.tsx): page list, accent-insensitive matching,
   the sections in display order (Telas + the server groups of POST /admin/search), the flat option list for the
   keyboard and index wrapping. */

export type PageEntry = { id: string; label: string; title?: string; group: string; path: string }

export type PaletteOption =
    | { kind: 'page'; id: string; page: PageEntry }
    | { kind: 'item'; id: string; group: SearchGroup['key']; item: SearchItem }
    | { kind: 'more'; id: string; group: SearchGroup['key']; label: string; path: string }

/** One block of the list: Telas (pages) or a server group, with its own note ("CRM não respondeu agora"). */
export type PaletteSection = { key: 'telas' | SearchGroup['key']; label: string; note?: string; options: PaletteOption[] }

export const OPEN_EVENT = 'cf-command-palette'

export const MAIN_PAGES: PageEntry[] = MAIN_PAGES_NAV.map(p => ({ id: p.id, label: p.label, title: p.title, group: p.group, path: p.to }))

/** Staff pages (/admin/*) the role can open (Hoje first, Denúncia last — everyone can report). No role = no admin entries. */
export function adminPages(role: AdminRole | null): PageEntry[] {
    if (!role) return []
    const pages: PageEntry[] = [{ id: 'a-hoje', label: 'Hoje', title: 'O que precisa de você hoje', group: 'Staff', path: '/staff/hoje' }]
    for (const area of ADMIN_AREAS) {
        if (!canOpenArea(role, area)) continue
        pages.push({ id: `a-${area.slug}`, label: area.label, title: area.title, group: `Staff · ${area.group}`, path: area.path })
        // Empresas e Igrejas: temas próprios no menu, com a permissão de Parceiros
        if (area.slug === 'parceiros') {
            pages.push({ id: 'a-empresas', label: 'Empresas', title: 'Parcerias corporativas', group: 'Staff · Parcerias corporativas', path: '/staff/empresas' })
            pages.push({ id: 'a-igrejas', label: 'Igrejas', title: 'Parcerias eclesiásticas', group: 'Staff · Parcerias eclesiásticas', path: '/staff/igrejas' })
        }
    }
    pages.push({ id: 'a-denuncia', label: 'Denúncia', title: 'Canais de denúncia', group: 'Staff', path: '/staff/denuncia' })
    return pages
}

/** Staff pages of the palette = the screens of the current frente menu (group = its area), each overview followed by
    the tabs its tree carries (Staff › Minha jornada…, `role` hides the leads-only ones) + Denúncia. */
export function staffPages(nav: readonly NavEntry[], frenteLabel: string, role: AdminRole | null = null): PageEntry[] {
    const pages: PageEntry[] = []
    for (const e of nav) {
        const group = e.area ? `${frenteLabel} · ${e.area}` : frenteLabel
        pages.push({ id: `s-${e.view.key}`, label: e.view.label, group, path: e.view.path })
        const xp = experienceOfView(e.view.key)
        if (!xp || xp.overview !== e.view.key) continue
        for (const t of experienceTreeTabs(xp.key)) {
            if (t.view !== e.view.key || !treeNodeAllowed(t, role)) continue
            pages.push({ id: `s-${e.view.key}-${t.tab}`, label: t.label, group, path: `${e.view.path}?tab=${encodeURIComponent(t.tab!)}` })
        }
    }
    pages.push({ id: 's-denuncia', label: 'Denúncia', title: 'Canais de denúncia', group: 'Staff', path: '/staff/denuncia' })
    return pages
}

/** Configurações pages (admin only): the old Admin space + the ERP settings sections. */
export function configPages(sections: readonly Pick<ConfigSection, 'key' | 'label' | 'group'>[]): PageEntry[] {
    return [
        ...MAIN_PAGES.map(p => ({ ...p, group: `Configurações · ${p.group}` })),
        ...sections.map(s => ({ id: `c-${s.key}`, label: s.label, group: `Configurações · ${s.group}`, path: `/configuracoes/${s.key}` })),
    ]
}

/** 0 = no match. Every query word must appear somewhere; label hits weigh more than title/group hits. */
export function scorePage(page: PageEntry, query: string): number {
    const q = normalize(query)
    if (!q) return 1
    const label = normalize(page.label)
    const rest = normalize(`${page.title ?? ''} ${page.group}`)
    const all = `${label} ${rest}`
    const words = q.split(' ')
    if (!words.every(w => all.includes(w))) return 0
    if (label === q) return 100
    if (label.startsWith(q)) return 80
    if (label.split(' ').some(w => w.startsWith(q))) return 60
    if (label.includes(q)) return 40
    if (words.every(w => label.includes(w))) return 30
    return 10
}

/** Pages matching the query, best first (stable for ties, so an empty query keeps the menu order). */
export function filterPages(pages: PageEntry[], query: string): PageEntry[] {
    return pages
        .map((page, i) => ({ page, i, s: scorePage(page, query) }))
        .filter(x => x.s > 0)
        .sort((a, b) => b.s - a.s || a.i - b.i)
        .map(x => x.page)
}

export const shouldSearch = (query: string) => query.trim().length >= SEARCH_ALL_MIN

/** Pages shown while typing (the whole menu with an empty query). */
export const PAGES_SHOWN = 5

/** Sections in display order. Telas go first when the query is empty or the best screen name (or one of its words)
    starts with it ("inadim"), otherwise after the groups (people and records first). Groups only from 2 chars; each keeps the server order,
    with "ver mais" as the last option when the server says there are more. */
export function buildSections(pages: PageEntry[], query: string, groups: readonly SearchGroup[] | undefined): PaletteSection[] {
    const q = query.trim()
    const matched = filterPages(pages, q)
    const shownPages = q ? matched.slice(0, PAGES_SHOWN) : matched
    const telas: PaletteSection | null = shownPages.length ? { key: 'telas', label: 'Telas', options: shownPages.map(page => ({ kind: 'page', id: page.id, page })) } : null
    const out: PaletteSection[] = []
    if (shouldSearch(q) && groups) {
        for (const g of groups) {
            const options: PaletteOption[] = g.items.map(item => ({ kind: 'item', id: `i-${item.id}`, group: g.key, item }))
            if (g.more) options.push({ kind: 'more', id: `m-${g.key}`, group: g.key, label: g.more.label, path: g.more.path })
            if (!options.length && !g.note) continue
            out.push({ key: g.key, label: g.label, ...(g.note ? { note: g.note } : {}), options })
        }
    }
    if (!telas) return out
    const strong = !q || scorePage(shownPages[0]!, q) >= 60
    return strong ? [telas, ...out] : [...out, telas]
}

/** Flat option list in display order (the keyboard walks it). */
export const flatOptions = (sections: readonly PaletteSection[]): PaletteOption[] => sections.flatMap(s => s.options)

/** Where an option goes. */
export const optionPath = (o: PaletteOption) => (o.kind === 'page' ? o.page.path : o.kind === 'item' ? o.item.path : o.path)

/** What the preview pane asks for (only results that have a related view). */
export const relatedOf = (o: PaletteOption | null | undefined): RelatedRef | null => (o?.kind === 'item' ? o.item.related : null)

/** Stable key of a related ref (the debounce and the query cache key). */
export const relatedKey = (r: RelatedRef | null) => (r ? `${r.kind}:${r.id}` : '')

/** Move the active index by delta, wrapping around; -1 when the list is empty. */
export function wrapIndex(current: number, delta: number, length: number): number {
    if (length <= 0) return -1
    if (current < 0) return delta >= 0 ? 0 : length - 1
    return (((current + delta) % length) + length) % length
}

export function isMacPlatform(platform: string): boolean {
    return /mac|iphone|ipad|ipod/i.test(platform)
}

/** ⌘K on mac, Ctrl+K elsewhere (either accepted everywhere, so external keyboards work too). */
export function isPaletteShortcut(e: { key: string; metaKey: boolean; ctrlKey: boolean; altKey: boolean; shiftKey: boolean }): boolean {
    return (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k'
}
